/* start - flights_split_bounds_us */
import React, { useCallback } from "react";
import SearchResultsWrapperDesktop from "./SearchResultsWrapper.desktop";
import { HiddenVisually, Stack, useViewport } from "@bookingcom/bui-react";
import SearchResultBanner from "../SearchResultBanner/SearchResultBanner";
import useSearchResultsDesktopController from "./useSearchResults.desktop.controller";
import SkeletonSearchDesktop, { SkeletonSearchContent } from "../Skeleton/SkeletonPages/SkeletonSearch.desktop";
import FailedSearchResults from "./FailedSearchResults.desktop";
import { RemoveDirectFlights } from "./components/RemoveDirectFlights";
import { TooManyFilters } from "./components/TooManyFilters";
import { NoResultsFound } from "./components/NoResultsFound";
import SearchResultsContentDesktop from "./SearchResultsContent.desktop";
import useCreateUrl from "../../../hooks/useCreateUrl";
import { useHistory, useParams } from "react-router-dom";
import { getLocationParams } from "./utils";
import SearchResultsListInboundDesktop from "./SearchResultsList.inbound.desktop";
import useOfferCartOrderData from "../../../hooks/useOfferCartOrderData";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { DestinationUkraineWarning } from "../TravelAlert";
import SirfInfoBanner from "../SirfInfoBanner";
import SearchBaggagePolicies from "../SearchBaggagePolicies";
import BackToOutbound from "./components/BackToOutbound";
import { UIFlightData } from "@flights/types";
import Frame from "../Frame";
import SearchSortSelector from "./components/SearchSortSelector";
import { trackWithDefaultStage } from "@flights/et-universal";

const Header = () => {
  const { isLarge } = useViewport();
  const isResponsiveFixes = !isLarge && !!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1);

  return (
    <Frame pb={4}>
      <Stack
        direction={isResponsiveFixes ? "column" : "row"}
        gap={isResponsiveFixes ? 4 : 2}
        alignItems={isResponsiveFixes ? "start" : "center"}
      >
        <Stack.Item grow={true}>
          <BackToOutbound />
        </Stack.Item>

        <SearchSortSelector />
      </Stack>
    </Frame>
  );
};

type Props = {
  onSelectFlight?: (flight: UIFlightData) => void;
};

const SearchResultsInboundDesktop = ({ onSelectFlight }: Props) => {
  const {
    getScreenReaderAnnouncementText,
    appliedFilters,
    isFirstLoad,
    canExtendSearch,
    onExpandToCitySearch,
    hasFlights,
    canRemoveDirectFlightsFilter,
    flights,
    fetchStatus,
    banners,
    isSirfAvailable,
    fetchedAt,
    searchCriteria
  } = useSearchResultsDesktopController();
  const i18n = useI18n();

  const humanRightBanner = banners?.find((banner) => banner.id === "LEGAL_NOTICE_HUMAN_RIGHTS");

  const { createUrl } = useCreateUrl();
  const history = useHistory();
  const { airports, outboundToken } = useParams<{ airports: string; outboundToken: string }>();
  const offerCartOrderData = useOfferCartOrderData();

  const onClickSelectFlight = useCallback(
    async (flight: any) => {
      onSelectFlight?.(flight);
      offerCartOrderData.set(flight.token, {
        offer: flight.token,
        cabinClass: searchCriteria?.cabinClass
      });
      const url = createUrl(
        `/flights/${airports}/inbound/${outboundToken}/${flight.token}/?${getLocationParams(location.search)}`
      );
      history.push(url);
    },
    [onSelectFlight, offerCartOrderData, searchCriteria?.cabinClass, createUrl, airports, outboundToken, history]
  );

  let resultingComp = undefined;

  if (fetchStatus === "initial" || fetchStatus === "loading") {
    if (isFirstLoad) {
      // this is the result of SSR
      return <SkeletonSearchDesktop />;
    }
    // need sidebar from now on and no SSR
    resultingComp = <SkeletonSearchContent />;
  }

  if (fetchStatus === "failed" && canExtendSearch) {
    // this can also be SSR
    return <FailedSearchResults onClickExpandToCitySearch={onExpandToCitySearch} />;
  }

  // case of success should go here, i.e. the composed result
  if (fetchStatus === "success") {
    if (hasFlights) {
      // No SSR for this case, why not ? because api is called from client side
      resultingComp = (
        <SearchResultsContentDesktop flights={flights} fetchedAt={fetchedAt}>
          <DestinationUkraineWarning />
          {isSirfAvailable && (
            <SirfInfoBanner
              bannerName="search-results"
              bannerContent={i18n.trans(t("flights_sr_spanish_discount_banner"))}
            />
          )}
          <SearchBaggagePolicies />
          <SearchResultsListInboundDesktop flights={flights} onSelectFlight={onClickSelectFlight} />
        </SearchResultsContentDesktop>
      );
    } else if (canRemoveDirectFlightsFilter) {
      // can be generated from SSR
      return (
        <>
          <SearchResultBanner banner={humanRightBanner} />
          <RemoveDirectFlights />
        </>
      );
    }
  }

  if (!resultingComp) {
    // this is a fallthrough default when everything else fails
    if (appliedFilters.length > 0) {
      // need sidebar here too
      resultingComp = <TooManyFilters />;
    } else {
      return <NoResultsFound />;
    }
  }

  return (
    <SearchResultsWrapperDesktop
      banners={
        <>
          {humanRightBanner && <SearchResultBanner banner={humanRightBanner} />}
          <Header />
        </>
      }
      showGenAiSummary={true}
    >
      {resultingComp}

      <HiddenVisually>
        <div aria-live="polite">{getScreenReaderAnnouncementText()}</div>
      </HiddenVisually>
    </SearchResultsWrapperDesktop>
  );
};

export default React.memo(SearchResultsInboundDesktop);
/* end - flights_split_bounds_us */
